import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Lock, AlertCircle, ArrowLeft } from 'lucide-react';
import { useResetPassword } from '../hooks/Account';
import { Header } from '@/components/Layout/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(8, 'Parola trebuie să aibă cel puțin 8 caractere!'),
    retypePassword: z.string().min(1, 'Vă rugăm să confirmați parola!'),
  })
  .refine((data) => data.newPassword === data.retypePassword, {
    message: 'Parolele nu se potrivesc!',
    path: ['retypePassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetPasswordMutation = useResetPassword();
  const [error, setError] = useState<string>('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Token de resetare lipsă. Vă rugăm să folosiți link-ul din email.');
    }
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) {
      setError('Token de resetare lipsă. Vă rugăm să folosiți link-ul din email.');
      return;
    }

    setError('');
    const result = await resetPasswordMutation.mutateAsync({
      token,
      newPassword: values.newPassword,
      retypePassword: values.retypePassword,
    });

    if (!result.isSuccess) {
      setError(result.errorMessage || 'Nu s-a putut reseta parola. Token-ul poate fi expirat.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-3">
            <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-gradient-to-br from-primary to-secondary mx-auto">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-center">Resetează parola</CardTitle>
            <CardDescription className="text-center">
              Introduceți noua parolă pentru contul dumneavoastră
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Eroare</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!token && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Token lipsă</AlertTitle>
                <AlertDescription>
                  Token-ul de resetare este lipsă. Vă rugăm să folosiți link-ul din email.
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Parolă nouă</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  {...register('newPassword')}
                  className={errors.newPassword ? 'border-destructive' : ''}
                />
                {errors.newPassword && (
                  <p className="text-sm text-destructive">{errors.newPassword.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="retypePassword">Confirmați parola</Label>
                <Input
                  id="retypePassword"
                  type="password"
                  placeholder="••••••••"
                  {...register('retypePassword')}
                  className={errors.retypePassword ? 'border-destructive' : ''}
                />
                {errors.retypePassword && (
                  <p className="text-sm text-destructive">{errors.retypePassword.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={resetPasswordMutation.isPending || !token}
              >
                {resetPasswordMutation.isPending ? 'Se resetează...' : 'Resetează parola'}
              </Button>
            </form>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:underline inline-flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Înapoi la autentificare
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
